interface FacilityPriceInfo {
  price: number;
  basePrice?: number; // 기본 이용가격 (시작종료형 전용)
  maxPrice?: number;
  discounts?: Array<{
    memberType: string | number;
    discountRate: number;
  }>;
}

interface ReservationTimeInfo {
  reservationType: 'time-slot' | 'start-end';
  startTime: number;
  endTime: number;
  reservationTimeUnit?: number;
}

export const FACILITY_RESERVATION_VAT_RATE = 0.1;

export interface FacilityPriceBreakdown {
  net: number;
  vat: number;
  total: number;
}

/**
 * 시설물 기본 가격을 계산합니다.
 * @param facility 시설물 가격 정보
 * @param reservation 예약 시간 정보
 * @returns 기본 가격 (할인 적용 전)
 */
export const calculateBasePrice = (
  facility: FacilityPriceInfo,
  reservation: ReservationTimeInfo
): number => {
  let basePrice = 0;
  
  if (reservation.reservationType === 'time-slot') {
    basePrice = facility.price;
  } else {
    // 시작종료형의 경우
    const hours = reservation.endTime - reservation.startTime;
    
    if (facility.basePrice && hours > 0) {
      // 기본 이용가격이 설정된 경우: 첫 1시간은 기본 이용가격, 나머지는 시간당 이용가격
      const additionalHours = Math.max(0, hours - 1);
      basePrice = facility.basePrice + (facility.price * additionalHours);
    } else {
      // 기본 이용가격이 없는 경우: 기존 로직 유지
      basePrice = facility.price * hours;
    }
    
    // 최대 가격 제한 적용
    if (facility.maxPrice && basePrice > facility.maxPrice) {
      basePrice = facility.maxPrice;
    }
  }
  
  return basePrice;
};

/**
 * 회원 등급별 할인율을 적용합니다.
 * @param basePrice 기본 가격
 * @param facility 시설물 가격 정보
 * @param userLevel 사용자 등급
 * @returns 할인이 적용된 가격
 */
export const applyDiscount = (
  basePrice: number,
  facility: FacilityPriceInfo,
  userLevel: number
): number => {
  const discount = findDiscountByMemberType(facility.discounts, userLevel);
  if (discount) {
    return Math.floor(basePrice * (1 - discount.discountRate / 100));
  }
  return basePrice;
};

const normalizeMemberType = (memberType: string | number | null | undefined): string => {
  return String(memberType ?? '').trim();
};

export const findDiscountByMemberType = (
  discounts: FacilityPriceInfo['discounts'],
  memberType: string | number | null | undefined
) => {
  const normalizedMemberType = normalizeMemberType(memberType);

  if (!discounts || !normalizedMemberType) {
    return undefined;
  }

  return discounts.find(
    discount => normalizeMemberType(discount.memberType) === normalizedMemberType
  );
};

/**
 * 최종 가격을 계산합니다.
 * @param facility 시설물 가격 정보
 * @param reservation 예약 시간 정보
 * @param userLevel 사용자 등급
 * @returns 최종 계산된 가격
 */
export const calculateFinalPrice = (
  facility: FacilityPriceInfo,
  reservation: ReservationTimeInfo,
  userLevel: number
): number => {
  const basePrice = calculateBasePrice(facility, reservation);
  return applyDiscount(basePrice, facility, userLevel);
};

export const calculateFinalPriceWithVAT = (
  facility: FacilityPriceInfo,
  reservation: ReservationTimeInfo,
  userLevel: number,
  vatRate: number = FACILITY_RESERVATION_VAT_RATE
): FacilityPriceBreakdown => {
  const net = calculateFinalPrice(facility, reservation, userLevel);
  const vat = Math.round(net * vatRate);

  return {
    net,
    vat,
    total: net + vat
  };
};
